import { useState, useEffect, useMemo } from 'react';
import { X, ChevronLeft, Image, WandSparkles, Sofa, Hammer, Upload, Users, Diamond, Sparkles, Trash2, GraduationCap, Crown, TreePine, Factory, Minimize2, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { uploadViaEdgeFunction } from '../../lib/gcs-client';
import { convertFileToWebP } from '../../lib/image-utils';
import { generateRender } from '../../lib/gemini-client';
import { supabase } from '../../lib/supabase-client';
import { toast } from 'sonner';
import { useAuth } from '../../lib/AuthContext';
import { useRenderOptions } from '../hooks/useRenderOptions';
import {
  trackTransformationStart,
  trackTransformationError,
} from '../../utils/gtm';
import {
  RenderMode,
  StagingPersona,
  RenovationStyle,
  WizardConfig,
} from '../../types/property';

interface RenderWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRenderGenerate?: (config: WizardConfig, createdImage?: { id?: string; publicUrl?: string }) => void;
  onGenerationStart?: () => void;
  onGenerationError?: (error: string) => void;
  initialImageUrl?: string;
  initialImageId?: string;
  initialRoomName?: string;
  initialGcsPath?: string;
  userId?: string;
  propertyId?: string;
}

const STYLE_IMAGES: Record<string, string> = {
  modern: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=400&h=300&fit=crop',
  vintage: 'https://images.unsplash.com/photo-1534349762230-e0cadf78f5da?w=400&h=300&fit=crop',
  scandinavian: 'https://images.unsplash.com/photo-1615875474908-f403134b6efe?w=400&h=300&fit=crop',
  industrial: 'https://images.unsplash.com/photo-1556912173-46c336c7fd55?w=400&h=300&fit=crop',
  default: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=400&h=300&fit=crop'
};

const DynamicIcon = ({ name, className }: { name: string | null; className?: string }) => {
  if (!name) return <Sparkles className={className} />;

  const icons: Record<string, React.ElementType> = {
    Sparkles, Users, Diamond, GraduationCap, Crown, TreePine, Factory, Minimize2,
    WandSparkles, Sofa, Hammer
  };

  const IconComponent = icons[name] || Sparkles;
  return <IconComponent className={className} strokeWidth={2} />;
};

export function RenderWizard({ open, onOpenChange, onRenderGenerate, onGenerationStart, onGenerationError, initialImageUrl, initialImageId, initialRoomName, initialGcsPath, userId, propertyId }: RenderWizardProps) {
  const [currentStep, setCurrentStep] = useState(initialImageUrl ? 2 : 1);
  const [selectedMode, setSelectedMode] = useState<RenderMode | null>(null);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [roomName, setRoomName] = useState(initialRoomName || '');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Przesyłanie zdjęcia...');
  const { user, profile } = useAuth();
  const credits = profile?.available_credits ?? 0;

  // Dynamic options from database
  const {
    enhanceToggles,
    stagingPersonas,
    renovationStyles,
    interiorStyles,
    loading: optionsLoading,
    getDefaultToggles,
    getPromptFragments,
    getStylePromptValue
  } = useRenderOptions();

  // Dynamic toggle states (keyed by toggle_key)
  const [toggleStates, setToggleStates] = useState<Record<string, boolean>>({});

  // Step 3 - Staging persona (dynamic key)
  const [selectedPersona, setSelectedPersona] = useState<string>('family');

  // Step 3 - Renovation style (dynamic key)
  const [selectedStyle, setSelectedStyle] = useState<string>('modern');

  // Initialize toggle states from database defaults when loaded
  useEffect(() => {
    if (enhanceToggles.length > 0 && Object.keys(toggleStates).length === 0) {
      setToggleStates(getDefaultToggles());
    }
  }, [enhanceToggles, getDefaultToggles, toggleStates]);

  // Set default persona/style from loaded options
  useEffect(() => {
    if (stagingPersonas.length > 0 && !stagingPersonas.find(p => p.option_key === selectedPersona)) {
      setSelectedPersona(stagingPersonas[0].option_key);
    }
  }, [stagingPersonas, selectedPersona]);

  useEffect(() => {
    if (renovationStyles.length > 0 && !renovationStyles.find(s => s.option_key === selectedStyle)) {
      setSelectedStyle(renovationStyles[0].option_key);
    }
  }, [renovationStyles, selectedStyle]);

  useEffect(() => {
    if (open) {
      setCurrentStep(initialImageUrl ? 2 : 1);
      setRoomName(initialRoomName || '');
      setUploadedImage(null);
      setSelectedMode(null);
      setIsLoading(false);
      // Reset to defaults
      setToggleStates(getDefaultToggles());
      if (stagingPersonas.length > 0) {
        setSelectedPersona(stagingPersonas[0].option_key);
      }
      if (renovationStyles.length > 0) {
        setSelectedStyle(renovationStyles[0].option_key);
      }
    }
  }, [open, initialImageUrl, initialRoomName, getDefaultToggles, stagingPersonas, renovationStyles]);

  // Legacy state for backward compatibility (will be removed after full migration)
  const autoRetouch = toggleStates['autoRetouch'] ?? true;
  const addAccessories = toggleStates['addAccessories'] ?? true;
  const setAutoRetouch = (v: boolean) => setToggleStates(s => ({ ...s, autoRetouch: v }));
  const setAddAccessories = (v: boolean) => setToggleStates(s => ({ ...s, addAccessories: v }));

  // If initialImageUrl is present, we need to handle it.
  // Ideally, convert URL to File if possible, or adjust logic to support URL only.
  // Since we can't easily convert URL to File without fetching (CORS), 
  // let's assume we proceed with just the URL if provided, but the component logic relies on uploadedImage being a File for preview.
  // We can treat initialImageUrl as a fallback for preview.

  // Note: We are not implementing full URL-to-File logic here, but enabling the flow to skip Step 1.
  // We need to store initialImageUrl in state if we want to use it later.
  // Let's assume onRenderGenerate can handle config without uploadedImage if we have context.
  // But RenderWizard logic expects uploadedImage.

  // Actually, we can just start at step 2 if initialImageUrl is provided.
  // And when generating config, we pass roomName but uploadedImage might be undefined.
  // The consumer (App.tsx) knows the context if it passed initialImageUrl.

  // Reset state when opening (if it was closed)
  // We can't use useEffect on 'open' easily without causing loops or complex state management.
  // But we can initialize state based on props.
  // If we want to support re-opening with clean state:

  // Let's just update the initial state logic above.

  const runGeneration = async (
    sourceImageUrl: string,
    sourceImageId: string | undefined,
    createdImage: { id?: string; publicUrl?: string; gcsPath?: string; originalPublicUrl?: string; variants?: any[] } | null,
    originalPublicUrl: string | undefined,
  ) => {
    trackTransformationStart(selectedMode!, credits);

    let style = '';
    if (selectedMode === 'enhance') {
      style = getPromptFragments(toggleStates);
    } else if (selectedMode === 'staging') {
      style = getStylePromptValue('staging', selectedPersona);
    } else if (selectedMode === 'renovation') {
      style = getStylePromptValue('renovation', selectedStyle);
    }

    setLoadingMessage('Uruchamianie generacji...');

    // Async orchestrator: returns generationId in ~2-5s, variants arrive via Realtime
    const generationResult = await generateRender({
      imageUrl: sourceImageUrl,
      mode: selectedMode!,
      style,
      roomName: roomName || 'Pomieszczenie',
      userId: userId!,
      propertyId: propertyId || 'unsorted',
      imageId: sourceImageId,
    });

    if (generationResult.success && generationResult.generationId) {
      // Build config with generationId — variants will arrive via Realtime subscription
      const config: WizardConfig = {
        mode: selectedMode!,
        uploadedImage: uploadedImage || undefined,
        roomName: roomName || undefined,
        initialImageId: initialImageId || undefined,
        generationId: generationResult.generationId,
      };

      if (selectedMode === 'enhance') {
        config.autoRetouch = autoRetouch;
        config.addAccessories = addAccessories;
      } else if (selectedMode === 'staging') {
        config.persona = selectedPersona as StagingPersona;
      } else if (selectedMode === 'renovation') {
        config.style = selectedStyle as RenovationStyle;
      }

      createdImage = {
        ...createdImage,
        originalPublicUrl: originalPublicUrl || initialImageUrl,
      };

      onRenderGenerate?.(config, createdImage || undefined);
    } else {
      const errorMessage = generationResult.error || 'nieznany błąd';
      trackTransformationError(selectedMode!, 'generation_failed', errorMessage);
      console.error('Generation failed:', generationResult.error);

      const errorCodeMessages: Record<string, string> = {
        RATE_LIMITED: 'Zbyt wiele zapytań. Poczekaj chwilę i spróbuj ponownie.',
        NO_CREDITS: 'Brak kredytów. Zakup plan lub doładuj kredyty.',
        RPC_ERROR: 'Wystąpił błąd serwera. Spróbuj ponownie.',
      };
      const userMessage = (generationResult.errorCode && errorCodeMessages[generationResult.errorCode])
        || 'Wystąpił błąd podczas uruchamiania generacji. Spróbuj ponownie później.';
      toast.error(userMessage);

      if (createdImage && !initialImageId) {
        const failedConfig: WizardConfig = {
          mode: selectedMode!,
          roomName: roomName || undefined,
          generationFailed: true,
        };
        if (selectedMode === 'enhance') {
          failedConfig.autoRetouch = autoRetouch;
          failedConfig.addAccessories = addAccessories;
        } else if (selectedMode === 'staging') {
          failedConfig.persona = selectedPersona as StagingPersona;
        } else if (selectedMode === 'renovation') {
          failedConfig.style = selectedStyle as RenovationStyle;
        }
        createdImage = {
          ...createdImage,
          originalPublicUrl: originalPublicUrl || initialImageUrl,
        };
        onRenderGenerate?.(failedConfig, createdImage || undefined);
      } else {
        onGenerationError?.(generationResult.error || 'Generation failed');
      }
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2 && selectedMode) {
      setCurrentStep(3);
    } else if (currentStep === 3) {
      // NON-ADMIN FLOW: Upload + generate directly
      onGenerationStart?.();
      handleClose();

      try {
        let gcsPath: string | undefined;
        let publicUrl: string | undefined;
        let createdImage: { id?: string; publicUrl?: string; gcsPath?: string; originalPublicUrl?: string; variants?: any[] } | null = null;

        // Upload image to GCS if we have an uploaded file
        if (uploadedImage && userId) {
          setLoadingMessage('Konwertowanie zdjęcia...');
          const { file: webpFile } = await convertFileToWebP(uploadedImage);

          setLoadingMessage('Przesyłanie zdjęcia do chmury...');
          const effectivePropertyId = propertyId || 'unsorted';

          const uploadResult = await uploadViaEdgeFunction(
            webpFile, 'wysko-originals', userId, effectivePropertyId
          );

          gcsPath = uploadResult.gcsPath;
          publicUrl = uploadResult.publicUrl;

          if (propertyId) {
            setLoadingMessage('Zapisywanie metadanych...');
            const { data, error } = await supabase
              .from('images')
              .insert({
                property_id: propertyId, user_id: userId, image_type: 'original',
                gcs_path: gcsPath, file_name: webpFile.name, file_size: webpFile.size,
                mime_type: webpFile.type, room_name: roomName || null,
                is_favorite: false, is_deleted: false,
              })
              .select().single();

            if (!error && data) {
              createdImage = { id: data.id, publicUrl, gcsPath };
              toast.success('Zdjęcie zostało przesłane');
            } else if (error) {
              console.error('Error saving image metadata:', error);
              toast.error('Nie udało się zapisać metadanych zdjęcia');
            }
          } else {
            createdImage = { publicUrl, gcsPath };
          }
        }

        const sourceImageUrl = publicUrl || initialImageUrl;
        if (!sourceImageUrl || !userId) {
          toast.error('Brak obrazu źródłowego do generowania.');
          onGenerationError?.('No source image URL');
          return;
        }

        const sourceImageId = createdImage?.id || initialImageId;

        if (!createdImage && initialImageUrl && initialImageId) {
          createdImage = { id: initialImageId, publicUrl: initialImageUrl, originalPublicUrl: initialImageUrl };
        }

        await runGeneration(sourceImageUrl, sourceImageId, createdImage, publicUrl);
      } catch (error) {
        trackTransformationError(selectedMode!, 'exception', error instanceof Error ? error.message : 'Unknown error');
        console.error('Error in render wizard:', error);
        toast.error('Wystąpił błąd podczas przetwarzania');
        onGenerationError?.(error instanceof Error ? error.message : 'Unknown error');
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    // Reset state
    setCurrentStep(initialImageUrl ? 2 : 1);
    setSelectedMode(null);
    setUploadedImage(null);
    setRoomName(initialRoomName || '');
    setAutoRetouch(true);
    setAddAccessories(true);
    setSelectedPersona('family');
    setSelectedStyle('modern');
    onOpenChange(false);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // S5-003 FIX: Validate file type and size
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Nieprawidłowy format pliku. Dozwolone: PNG, JPG, WebP');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Plik jest za duży. Maksymalny rozmiar to 10MB');
      return;
    }

    setUploadedImage(file);
  };

  // S5-002 FIX: Memoize blob URL and revoke on cleanup to prevent memory leak
  const previewUrl = useMemo(() => uploadedImage ? URL.createObjectURL(uploadedImage) : null, [uploadedImage]);
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const canProceed = () => {
    if (currentStep === 1) return uploadedImage !== null;
    if (currentStep === 2) return selectedMode !== null;
    return true;
  };

  const getButtonLabel = () => {
    if (currentStep === 3) return 'Utwórz rendery';
    return 'Dalej';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[960px] p-0 gap-0" hideCloseButton>
        {isLoading ? (
          /* LOADING STATE */
          <div className="flex flex-col items-center justify-center min-h-[500px] px-8">
            <div className="relative w-16 h-16 mb-6">
              {/* Circular loader */}
              <div className="absolute inset-0 border-4 border-border rounded-full"></div>
              <div className="absolute inset-0 border-4 border-foreground border-t-transparent rounded-full animate-spin"></div>
            </div>
            <div className="text-center space-y-2">
              <p className="text-[15px] font-medium animate-pulse">
                {loadingMessage}
              </p>
              <p className="text-[13px] text-muted-foreground">
                Proszę czekać...
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <DialogHeader className="px-8 pt-8 pb-4 border-b border-border">
              <div className="flex items-center justify-between">
                <DialogTitle className="text-[20px]">Nowy Projekt</DialogTitle>
                <button
                  onClick={handleClose}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-accent transition-colors"
                >
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
              <DialogDescription className="sr-only">
                Kreator tworzenia nowych renderów nieruchomości w trzech krokach: przesyłanie zdjęcia, wybór trybu renderowania, oraz konfiguracja parametrów
              </DialogDescription>
              {/* Progress Indicator */}
              <div className="mt-4">
                <p className="text-[13px] text-muted-foreground">
                  Krok {currentStep} z 3
                </p>
              </div>
            </DialogHeader>

            {/* Content Area */}
            <div className="px-8 py-8 min-h-[500px]">
              {/* STEP 1: Upload */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <h3 className="text-[16px] font-medium">Prześlij zdjęcie</h3>

                  {!uploadedImage ? (
                    // Upload dropzone
                    <label
                      htmlFor="image-upload"
                      className="flex flex-col items-center justify-center w-full h-[420px] border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex flex-col items-center justify-center gap-3">
                        <Upload className="w-12 h-12 text-muted-foreground" strokeWidth={1.5} />
                        <div className="text-center">
                          <p className="text-[14px] font-medium">
                            Kliknij aby wybrać zdjęcie
                          </p>
                          <p className="text-[12px] text-muted-foreground mt-1">
                            PNG, JPG, WebP do 10MB
                          </p>
                        </div>
                      </div>
                      <input
                        id="image-upload"
                        type="file"
                        className="hidden"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handleImageUpload}
                      />
                    </label>
                  ) : (
                    // File selected state with thumbnail
                    <div className="space-y-4">
                      <div className="w-full h-[320px] border-2 border-border rounded-lg bg-card overflow-hidden">
                        <div className="relative w-full h-full">
                          <img
                            src={previewUrl || ''}
                            alt="Uploaded preview"
                            className="w-full h-full object-contain"
                          />
                          {/* File info overlay */}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                            <div className="flex items-center justify-between">
                              <div className="text-white">
                                <p className="text-[14px] font-medium mb-1">{uploadedImage.name}</p>
                                <p className="text-[12px] text-white/70">
                                  {(uploadedImage.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
                              <button
                                onClick={() => setUploadedImage(null)}
                                className="flex items-center gap-2 px-4 py-2 rounded-md bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-colors"
                              >
                                <Trash2 className="w-4 h-4" strokeWidth={2} />
                                <span className="text-[13px]">Usuń</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Room Name Input */}
                      <div className="space-y-2">
                        <Label htmlFor="room-name" className="text-[14px]">Nazwa pomieszczenia (opcjonalnie)</Label>
                        <input
                          id="room-name"
                          type="text"
                          value={roomName}
                          onChange={(e) => setRoomName(e.target.value)}
                          placeholder="np. Salon, Kuchnia, Sypialnia..."
                          className="w-full h-10 px-3 rounded-md border border-border bg-background text-[14px] focus:outline-none focus:ring-1 focus:ring-foreground"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 2: Mode Selection */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <h3 className="text-[16px] font-medium">Wybierz tryb pracy</h3>

                  <div className="space-y-3">
                    {/* Large Card - Enhancement */}
                    <button
                      onClick={() => setSelectedMode('enhance')}
                      className={`w-full p-6 rounded-lg border-2 transition-all text-left hover:shadow-md hover:-translate-y-0.5 ${selectedMode === 'enhance'
                        ? 'border-foreground bg-accent'
                        : 'border-border bg-card'
                        }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-blue-50 text-blue-600">
                          <div className="relative">
                            <Image className="w-6 h-6" strokeWidth={2} />
                            <WandSparkles className="w-4 h-4 absolute -top-1 -right-1" strokeWidth={2} />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-[15px] font-medium mb-1">Poprawa Jakości</h4>
                          <p className="text-[13px] text-muted-foreground">
                            Oświetlenie, kolory, detale, akcesoria
                          </p>
                        </div>
                      </div>
                    </button>

                    {/* Two Smaller Cards */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Staging */}
                      <button
                        onClick={() => setSelectedMode('staging')}
                        className={`p-5 rounded-lg border-2 transition-all text-left hover:shadow-md hover:-translate-y-0.5 ${selectedMode === 'staging'
                          ? 'border-foreground bg-accent'
                          : 'border-border bg-card'
                          }`}
                      >
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-50 text-purple-600">
                            <Sofa className="w-5 h-5" strokeWidth={2} />
                          </div>
                          <div>
                            <h4 className="text-[14px] font-medium mb-1">Wirtualne Umeblowanie</h4>
                            <p className="text-[12px] text-muted-foreground">
                              Wstaw meble do pustych wnętrz
                            </p>
                          </div>
                        </div>
                      </button>

                      {/* Renovation */}
                      <button
                        onClick={() => setSelectedMode('renovation')}
                        className={`p-5 rounded-lg border-2 transition-all text-left hover:shadow-md hover:-translate-y-0.5 ${selectedMode === 'renovation'
                          ? 'border-foreground bg-accent'
                          : 'border-border bg-card'
                          }`}
                      >
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-50 text-orange-600">
                            <Hammer className="w-5 h-5" strokeWidth={2} />
                          </div>
                          <div>
                            <h4 className="text-[14px] font-medium mb-1">Renowacja</h4>
                            <p className="text-[12px] text-muted-foreground">
                              Zmień styl wykończenia
                            </p>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3: Configuration (Conditional) */}
              {currentStep === 3 && selectedMode === 'enhance' && (
                <div className="space-y-6">
                  <h3 className="text-[16px] font-medium">Opcje poprawy jakości</h3>

                  <div className="space-y-4">
                    {optionsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      enhanceToggles.map(toggle => (
                        <div key={toggle.id} className="flex items-center justify-between p-4 rounded-lg border border-border bg-card">
                          <Label htmlFor={toggle.toggle_key} className="text-[14px] cursor-pointer">
                            {toggle.name}
                          </Label>
                          <Switch
                            id={toggle.toggle_key}
                            checked={toggleStates[toggle.toggle_key] ?? toggle.default_enabled}
                            onCheckedChange={(checked) => setToggleStates(prev => ({ ...prev, [toggle.toggle_key]: checked }))}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {currentStep === 3 && selectedMode === 'staging' && (
                <div className="space-y-6">
                  <h3 className="text-[16px] font-medium">Wybierz grupę docelową</h3>

                  <div className="grid grid-cols-3 gap-3">
                    {optionsLoading ? (
                      <div className="col-span-3 flex items-center justify-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      stagingPersonas.map(persona => (
                        <button
                          key={persona.id}
                          onClick={() => setSelectedPersona(persona.option_key)}
                          className={`p-5 rounded-lg border-2 transition-all hover:shadow-md hover:-translate-y-0.5 ${selectedPersona === persona.option_key
                            ? 'border-foreground bg-accent'
                            : 'border-border bg-card'
                            }`}
                        >
                          <div className="flex flex-col items-center gap-3">
                            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-gray-50 text-gray-600">
                              <DynamicIcon name={persona.icon} className="w-6 h-6" />
                            </div>
                            <p className="text-[13px] font-medium">{persona.name}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}

              {currentStep === 3 && selectedMode === 'renovation' && (
                <div className="space-y-6">
                  <h3 className="text-[16px] font-medium">Wybierz styl wnętrza</h3>

                  <div className="grid grid-cols-2 gap-3">
                    {optionsLoading ? (
                      <div className="col-span-2 flex items-center justify-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      renovationStyles.map(style => (
                        <button
                          key={style.id}
                          onClick={() => setSelectedStyle(style.option_key)}
                          className={`h-[120px] rounded-lg border-2 transition-all relative overflow-hidden hover:shadow-md hover:-translate-y-0.5 ${selectedStyle === style.option_key
                            ? 'border-foreground'
                            : 'border-border'
                            }`}
                          style={{
                            backgroundImage: `url(${STYLE_IMAGES[style.option_key] || STYLE_IMAGES.default})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                            <p className="text-white font-medium text-[14px]">{style.name}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}

            </div>

            {/* Footer */}
            <div className="px-8 py-5 border-t border-border flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={currentStep === 1 || (currentStep === 2 && !!initialImageUrl)}
                className="gap-2"
              >
                <ChevronLeft className="w-4 h-4" strokeWidth={2} />
                Wstecz
              </Button>

              <Button
                onClick={handleNext}
                disabled={!canProceed() || (currentStep === 3 && credits <= 0)}
                className="gap-2 min-w-[160px]"
              >
                {(currentStep === 3 && credits <= 0) ? (
                  <span className="text-red-500 font-medium">Brak kredytów</span>
                ) : (
                  getButtonLabel()
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}